import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { appSettings } from '@app/core/constants/appSettings';
import { regEx } from '@app/core/constants/regEx';
import { validationMsgs } from '@app/core/constants/validationMsgs';
import { DataService } from '@app/core/services/data.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { delay, map, of } from 'rxjs';
import { ApplicationFormDataService } from '../application-form/application-form-data.service';
import { ApplicationFormService } from '../application-form/application-form.service';

@Injectable({
   providedIn: 'root',
})
export class ApplicantService {
   constructor(
      private afs: ApplicationFormService,
      private dataService: DataService,
      private afds: ApplicationFormDataService
   ) {}

   yesNoMap = [
      {
         value: false,
         name: 'No',
      },
      {
         value: true,
         name: 'Yes',
      },
   ];

   cachedNiNumber: null | string = null;

   public setCachedNiNumber(niNumber: string) {
      this.cachedNiNumber = niNumber;
   }

   fetchSchema(referenceTypes: any, changeFns: any, globalValidators: any) {
      return [
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            validators: {
               // we can use any key here; using a known key that error summary can pick up
               previousAddresses: {
                  expression: (c: AbstractControl, f: FormlyFieldConfig) => globalValidators['lengthOfTimeAtProperty'](c, f),
                  title: 'Address history',
                  msg: 'Please provide further address history',
               },
            },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: {
                     htmlClass: 'row',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-12',
                        },
                        fieldGroup: [
                           {
                              key: 'isJointApplication',
                              type: 'radiobuttons',
                              props: {
                                 change: (fieldConfig: FormlyFieldConfig) => {
                                    changeFns['jointApplication'](fieldConfig.formControl!.value);
                                 },
                                 options: this.yesNoMap,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 required: true,
                                 title: 'Would you like to make a joint application? (A joint tenancy is when two individuals have equal rights & responsibilities for the property they have mutually signed for.) ',
                              },
                           },
                           {
                              key: 'applicationBy3rdParty',
                              type: 'radiobuttons',
                              props: {
                                 options: this.yesNoMap,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 required: true,
                                 title: 'Is application to be completed by a 3rd party?',
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'About you' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: {
                     htmlClass: 'row',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'title',
                              type: 'select',
                              props: {
                                 title: 'Title',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_titletype'),
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'firstName',
                              type: 'input',
                              props: {
                                 title: 'First Name',
                                 required: true,
                                 minLength: 0,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
               },
               {
                  wrappers: ['section'],
                  props: {
                     htmlClass: 'row',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'middleName',
                              type: 'input',
                              props: {
                                 title: 'Middle Name',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'lastName',
                              type: 'input',
                              props: {
                                 title: 'Last Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            fieldGroup: [
               {
                  key: 'isPreviousName',
                  type: 'radiobuttons',
                  props: {
                     title: 'Have you ever been known by another name?',
                     required: true,
                     options: this.yesNoMap,
                     labelHtmlClass: 'control-label',
                     activeClass: 'active',
                  },
               },
               {
                  wrappers: ['section'],
                  props: {
                     htmlClass: 'row',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'previousTitle',
                              type: 'select',
                              props: {
                                 required: true,
                                 title: 'Title',
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_titletype'),
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'previousFirstName',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'First Name',
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: '!model.isPreviousName',
                  },
               },
               {
                  wrappers: ['section'],
                  props: {
                     htmlClass: 'row',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'previousMiddleName',
                              type: 'input',
                              props: {
                                 title: 'Middle Name',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: {
                           htmlClass: 'col-sm-6',
                        },
                        fieldGroup: [
                           {
                              key: 'previousLastName',
                              type: 'input',
                              props: {
                                 title: 'Last Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: '!model.isPreviousName',
                  },
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'mobilePhoneNumber',
                              type: 'input',
                              props: {
                                 title: 'Mobile Phone Number',
                                 required: true,
                                 maxLength: 15,
                                 pattern: regEx.mobilePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.phone,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'otherPhoneNumber',
                              type: 'input',
                              props: {
                                 title: 'Other Phone Number',
                                 maxLength: 15,
                                 pattern: regEx.phonePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.phone,
                                 },
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'isProbity',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are you or any of your friends or family an employee or board member of Your Housing Group?',
                                 required: true,
                                 options: this.yesNoMap,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                              },
                           },
                        ],
                     },
                  ],
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityConnectionTypeId',
                              type: 'select',
                              props: {
                                 title: 'What is the nature of that connection?',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_probityconnectiontype'),
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: '!model.isProbity',
                  },
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityTitle',
                              type: 'select',
                              props: {
                                 title: 'Employee Title',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_titletype'),
                              },
                           },
                           {
                              key: 'probityFirstName',
                              type: 'input',
                              props: {
                                 title: 'Employee First Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'probityLastName',
                              type: 'input',
                              props: {
                                 title: 'Employee Last Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityRelationship',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Relationship',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'probityEmployeeRole',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Employee Role',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: (field: FormlyFieldConfig) => !(field.model.probityConnectionTypeId === appSettings.probityConnectionTypeToEmployee),
                  },
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityTitle',
                              type: 'select',
                              props: {
                                 title: 'Board Member Title',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_titletype'),
                              },
                           },
                           {
                              key: 'probityFirstName',
                              type: 'input',
                              props: {
                                 title: 'Board Member First Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'probityLastName',
                              type: 'input',
                              props: {
                                 title: 'Board Member Last Name',
                                 required: true,
                                 maxLength: 35,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityRelationship',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Relationship',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'probityPositionOnBoard',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Board Member Position',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: (field: FormlyFieldConfig) => !(field.model.probityConnectionTypeId === appSettings.probityConnectionTypeToBoardMember),
                  },
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityPositionOnBoard',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Position on Board',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: (field: FormlyFieldConfig) => !(field.model.probityConnectionTypeId === appSettings.probityConnectionTypeBoardMember),
                  },
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'probityJobTitle',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Job Title',
                                 maxLength: 50,
                                 pattern: regEx.namePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                  ],
                  expressions: {
                     hide: (field: FormlyFieldConfig) => !(field.model.probityConnectionTypeId === appSettings.probityConnectionTypeEmployee),
                  },
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'niNo',
                              type: 'input',
                              props: {
                                 supportingText: '(this should be entered in the format XX000000X) ',
                                 title: 'National Insurance Number',
                                 required: true,
                                 pattern: regEx.nationalInsuranceNumberPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.niNumber,
                                 },
                              },
                              asyncValidators: {
                                 niNumber: {
                                    expression: (control: AbstractControl) => {
                                       //workaround to prevent reactive form conditionals triggering ni validation
                                       //the async validator becomes guarded against and runs 1 step behind the change cycle
                                       if (this.cachedNiNumber === control.value) {
                                          return of(true);
                                       } else {
                                          this.setCachedNiNumber(control.value);
                                       }

                                       const leadApplicant: any = this.afs.getLeadModel();
                                       const contactid: any = leadApplicant.contact;

                                       return this.afds.getNationalInsuranceNumber(control.value, contactid).pipe(
                                          delay(500),
                                          map((res) => {
                                             if (res.status === 409) {
                                                return false;
                                             }

                                             return true;
                                          })
                                       );
                                    },
                                    message: 'National Insurance number already exists please contact the Lettings Team',
                                 },
                              },
                              parsers: [
                                 (value: any) => {
                                    return value?.toUpperCase();
                                 },
                              ],
                              modelOptions: {
                                 updateOn: 'blur',
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'nationality',
                              type: 'select',
                              props: {
                                 title: 'Nationality',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'vw_nationalitytype_shortlist'),
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'gender',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Gender',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_gendertype'),
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'dateOfBirth',
                              type: 'datepicker',
                              props: {
                                 title: 'Date of Birth',
                                 supportingText: 'dd/mm/yyyy',
                                 minAge: 18,
                                 maxAge: 120,
                                 required: true,
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Current Address' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'addressLine1',
                              type: 'input',
                              props: {
                                 title: 'House name or number',
                                 required: true,
                                 maxLength: 40,
                                 pattern: regEx.alphaNumericPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaNumeric,
                                 },
                              },
                           },
                           {
                              key: 'addressLine2',
                              type: 'input',
                              props: {
                                 title: 'Street',
                                 maxLength: 40,
                                 pattern: regEx.addressPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaNumeric,
                                 },
                              },
                           },
                           {
                              key: 'addressLine3',
                              type: 'input',
                              props: {
                                 title: 'Address Line 3',
                                 maxLength: 40,
                                 pattern: regEx.addressPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaNumeric,
                                 },
                              },
                           },
                           {
                              key: 'city',
                              type: 'input',
                              props: {
                                 title: 'City',
                                 maxLength: 40,
                                 pattern: regEx.alphaPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'county',
                              type: 'input',
                              props: {
                                 title: 'County',
                                 maxLength: 40,
                                 pattern: regEx.alphaPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'country',
                              type: 'input',
                              props: {
                                 title: 'Country',
                                 maxLength: 80,
                                 pattern: regEx.countryPattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.alphaOnly,
                                 },
                              },
                           },
                           {
                              key: 'postCode',
                              type: 'input',
                              props: {
                                 title: 'Postcode',
                                 required: true,
                                 maxLength: 8,
                                 pattern: regEx.postCodePattern,
                              },
                              validation: {
                                 messages: {
                                    pattern: validationMsgs.postcode,
                                 },
                              },
                              parsers: [
                                 (value: any) => {
                                    return value?.toUpperCase();
                                 },
                              ],
                           },
                           {
                              key: 'durationAtCurrentProperty',
                              type: 'duration',
                              props: {
                                 title: 'How long have you lived at the property?',
                                 required: true,
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section' },
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.durationAtCurrentProperty < 60),
            },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'row' },
                        fieldGroup: [
                           {
                              key: 'previousAddresses',
                              defaultValue: [null],
                              type: 'array',
                              props: {
                                 add: 'Add a new address',
                                 itemTitle: 'Previous Address',
                                 style: {
                                    add: 'btn-success',
                                 },
                                 validationMessage: 'Please provide further address history',
                              },
                              fieldArray: {
                                 fieldGroup: [
                                    {
                                       wrappers: ['section'],
                                       fieldGroup: [
                                          {
                                             wrappers: ['section'],
                                             fieldGroup: [
                                                {
                                                   wrappers: ['section'],
                                                   props: { htmlClass: 'col-sm-6' },
                                                   fieldGroup: [
                                                      {
                                                         key: 'addressLine1',
                                                         type: 'input',
                                                         props: {
                                                            title: 'Address Line 1',
                                                            required: true,
                                                            maxLength: 40,
                                                            pattern: regEx.alphaNumericPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaNumeric,
                                                            },
                                                         },
                                                      },
                                                      {
                                                         key: 'addressLine2',
                                                         type: 'input',
                                                         props: {
                                                            title: 'Address Line 2',
                                                            maxLength: 40,
                                                            pattern: regEx.addressPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaNumeric,
                                                            },
                                                         },
                                                      },
                                                      {
                                                         key: 'addressLine3',
                                                         type: 'input',
                                                         props: {
                                                            title: 'Address Line 3',
                                                            maxLength: 40,
                                                            pattern: regEx.addressPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaNumeric,
                                                            },
                                                         },
                                                      },
                                                      {
                                                         key: 'city',
                                                         type: 'input',
                                                         props: {
                                                            title: 'City',
                                                            maxLength: 40,
                                                            pattern: regEx.alphaPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaOnly,
                                                            },
                                                         },
                                                      },
                                                   ],
                                                },
                                                {
                                                   wrappers: ['section'],
                                                   props: { htmlClass: 'col-sm-6' },
                                                   fieldGroup: [
                                                      {
                                                         key: 'county',
                                                         type: 'input',
                                                         props: {
                                                            title: 'County',
                                                            maxLength: 40,
                                                            pattern: regEx.alphaPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaOnly,
                                                            },
                                                         },
                                                      },
                                                      {
                                                         key: 'country',
                                                         type: 'input',
                                                         props: {
                                                            title: 'Country',
                                                            maxLength: 80,
                                                            pattern: regEx.countryPattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.alphaOnly,
                                                            },
                                                         },
                                                      },
                                                      {
                                                         key: 'postCode',
                                                         type: 'input',
                                                         props: {
                                                            title: 'Postcode',
                                                            required: true,
                                                            maxLength: 8,
                                                            pattern: regEx.postCodePattern,
                                                         },
                                                         validation: {
                                                            messages: {
                                                               pattern: validationMsgs.postcode,
                                                            },
                                                         },
                                                         parsers: [
                                                            (value: any) => {
                                                               return (value = value.toUpperCase());
                                                            },
                                                         ],
                                                      },
                                                      {
                                                         key: 'durationAtPreviousProperty',
                                                         type: 'duration',
                                                         props: {
                                                            title: 'How long have you lived at the previous property?',
                                                            required: true,
                                                         },
                                                      },
                                                   ],
                                                },
                                             ],
                                          },
                                       ],
                                    },
                                 ],
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Living Arrangements' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'livingArrangementsTypeId',
                              type: 'select',
                              props: {
                                 title: 'Which best describes your current living arrangements?',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_livingarrangementstype'),
                              },
                           },
                           {
                              key: 'reasonForMovingTypeId',
                              type: 'select',
                              props: {
                                 title: 'What is your reason for moving?',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_reasonformovingtype'),
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !(field.model.reasonForMoving === appSettings.cantAffordRentMortgage),
                              },
                              key: 'unaffordabilityReason',
                              type: 'select',
                              props: {
                                 title: "Please state why you can't afford your rent or mortgage",
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_unaffordabilityreasontype'),
                              },
                           },
                           {
                              key: 'isCouncilHelping',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are the council currently looking for somewhere for you to live?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Property Ownership
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'ops'),
            },
            wrappers: ['connectSection'],
            props: {
               htmlClass: 'section',
               title: 'Property Ownership',
               subtitle:
                  'Is your property currently up for sale? In line with fair access to social housing, you are not eligible to rent social housing.\n\n\nIf you have equity in property over a certain threshold.This amount does vary in different local authorities, your equity will be reviewed upon application to ascertain if you are eligible for a property to rent, shared ownership or outright sale.',
            },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'homeOwner',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are you a home owner?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },

                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !(field.model.homeOwner == true),
                              },
                              key: 'propertyUpForSale',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Is your property on the market?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.homeOwner,
                              },
                              key: 'haveMortgage',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Do you still have a mortgage?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.homeOwner,
                              },
                              key: 'homeValue',
                              type: 'input',
                              props: {
                                 title: 'What is the value of your property?',
                                 required: true,
                                 fieldAddonLeft: '£',
                                 numberType: true,
                              },
                              parsers: [
                                 (value: any) => {
                                    if (parseFloat(value)) {
                                       return Math.abs(Math.floor(value));
                                    } else {
                                       return value;
                                    }
                                 },
                              ],
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.homeOwner,
                              },
                              key: 'savingsValue',
                              type: 'input',
                              props: {
                                 title: 'What is the value of your savings?',
                                 required: true,
                                 fieldAddonLeft: '£',
                                 numberType: true,
                              },
                              parsers: [
                                 (value: any) => {
                                    if (parseFloat(value)) {
                                       return Math.abs(Math.floor(value));
                                    } else {
                                       return value;
                                    }
                                 },
                              ],
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.homeOwner,
                              },

                              key: 'haveEquityRelease',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Do you have equity release?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.haveEquityRelease,
                              },
                              key: 'equityReleaseValue',
                              type: 'input',
                              props: {
                                 title: 'What is the value of the equity release?',
                                 required: true,
                                 fieldAddonLeft: '£',
                                 numberType: true,
                              },
                              parsers: [
                                 (value: any) => {
                                    if (parseFloat(value)) {
                                       return Math.abs(Math.floor(value));
                                    } else {
                                       return value;
                                    }
                                 },
                              ],
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !field.model?.homeOwner,
                                 'model.totalEquity': (field: FormlyFieldConfig) =>
                                    (field.model?.homeValue ?? 0) + (field.model?.savingsValue ?? 0) - (field.model?.equityReleaseValue ?? 0),
                              },
                              key: 'totalEquity',
                              type: 'input',
                              props: {
                                 title: 'Total Equity',
                                 required: true,
                                 fieldAddonLeft: '£',
                                 numberType: true,
                                 disabled: true,
                              },

                              parsers: [
                                 (value: any) => {
                                    if (parseFloat(value)) {
                                       return Math.abs(Math.floor(value));
                                    } else {
                                       return value;
                                    }
                                 },
                              ],
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Mobility and Vehicles
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'ops'),
            },
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Mobility and Vehicles' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'ownMotorVehicle',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Do you own a motor vehicle?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: '!model.ownMotorVehicle',
                              },
                              type: 'help',
                              props: {
                                 htmlClass: 'help-block form-info',
                                 helpvalue:
                                    'Please be aware that not all properties have on-site parking, and that parking is not allocated to individual residents.',
                              },
                           },
                           {
                              key: 'haveMobilityScooter',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Do you have a mobility scooter?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: '!model.haveMobilityScooter',
                              },
                              type: 'help',
                              props: {
                                 htmlClass: 'help-block form-info',
                                 helpvalue:
                                    'Please be aware that not all properties can accommodate mobility scooters, this will need to be discussed further during the viewing.',
                              },
                           },
                           {
                              expressions: {
                                 hide: '!model.haveMobilityScooter',
                              },
                              key: 'haveMobilityScooterInsurance',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Do you have Mobility Scooter Insurance?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) =>
                                    !(field.model.haveMobilityScooter === true && field.model.haveMobilityScooterInsurance === false),
                              },
                              type: 'help',
                              props: {
                                 htmlClass: 'help-block form-info',
                                 helpvalue: 'Please be aware that this is a requirement before permission can be granted to keep a mobility scooter.',
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'supportTravelMinimumDistance',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are you able to travel a minimum distance of 7.5 meters from your home to the nearest fire break either independently or with your usual mobility aids?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportTravelMinimumDistance !== false,
                              },
                              type: 'help',
                              props: {
                                 htmlClass: 'help-block form-info',
                                 helpvalue:
                                    'Please be aware that this is an essential fire safety requirement, and we will need to assess this further as we may not be able to allocate a suitable property to you.',
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportTravelMinimumDistance !== false,
                              },
                              key: 'supportTravelMinimumDistanceDetails',
                              type: 'textarea',
                              props: {
                                 required: true,
                                 title: 'Details',
                                 maxLength: 2000,
                              },
                           },
                           {
                              key: 'supportTravelMinimumDistanceHousehold',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are all household members able to travel a minimum distance of 7.5 meters from your home to the nearest fire break either independently or with their usual mobility aids?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportTravelMinimumDistanceHousehold !== false,
                              },
                              type: 'help',
                              props: {
                                 htmlClass: 'help-block form-info',
                                 helpvalue:
                                    'Please be aware that this is an essential fire safety requirement, and we will need to assess this further as we may not be able to allocate a suitable property to you.',
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportTravelMinimumDistanceHousehold !== false,
                              },
                              key: 'supportTravelMinimumDistanceHouseholdDetails',
                              type: 'textarea',
                              props: {
                                 required: true,
                                 title: 'Details',
                                 maxLength: 2000,
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'mobilityUnderstandAndAgree',
                              type: 'checkbox',
                              props: {
                                 title: 'I have understood and agree with the information I have provided',
                                 // required: true
                              },
                              validators: {
                                 validation: ['requiredTrue'],
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Contact Preferences
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Contact Preferences' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'preferredContactMethod',
                              type: 'select',
                              props: {
                                 title: 'Preferred Method of Contact',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'preferredcontactmethodcode'),
                              },
                           },
                           {
                              key: 'preferredLanguage',
                              type: 'select',
                              props: {
                                 title: 'Preferred language',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_language'),
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => !(field.model.preferredLanguage == 875970001),
                              },
                              key: 'preferredLanguageOther',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Preferred language other',
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Residency
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'ops'),
            },
            wrappers: ['connectSection'],
            props: {
               htmlClass: 'section',
               title: 'Residency',
               subtitle:
                  'Before you can rent in England, you need to prove your right to rent to your landlord. How you prove your right to rent depends on your nationality and your immigration status. You will need to bring proof to the viewing. For further info see https://www.gov.uk/prove-right-to-rent  ',
            },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'ukResident',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are you a UK Resident?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.ukResident !== false,
                              },
                              key: 'eligibleToRemainInTheUk',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Are you eligible to remain in the UK?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                        ],
                     },
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              type: 'help',
                              props: {
                                 helpvalue: '<h3>Which of the following are you able to supply at the viewing?</h3>',
                              },
                           },
                           {
                              key: 'passport',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Passport',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              key: 'drivingLicence',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Driving Licence',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              key: 'biometricCard',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Bio metric card',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              key: 'bankStatement',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Bank Statement',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              key: 'rtrCode',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Right To Rent Code',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                                 description:
                                    'If you’re not a British or Irish citizen, you can usually get a share code to prove your right to rent. See https://www.gov.uk/prove-right-to-rent for more details.',
                              },
                           },
                           {
                              key: 'other',
                              type: 'radiobuttons',
                              props: {
                                 title: 'Other',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                           {
                              expressions: {
                                 hide: '!model.other',
                              },
                              key: 'otherWhat',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Other What',
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Support,
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'ops'),
            },
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Support' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-6' },
                        fieldGroup: [
                           {
                              key: 'supportEnterHomeEmergency',
                              type: 'radiobuttons',
                              props: {
                                 title: 'May we enter your home in an emergency?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                                 description:
                                    'For your safety and wellbeing, it is important that we have permission to enter your home in an emergency. This might be when you press your alarm button in an emergency; or if we become concerned about your welfare and you cannot be contacted by phone, etc; and if we need to make emergency repairs. Do you give permission for us to enter your home in an emergency?',
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportEnterHomeEmergency !== false,
                              },
                              key: 'supportEnterHomeEmergencyReason',
                              type: 'textarea',
                              props: {
                                 required: true,
                                 title: 'Reason for refusal',
                                 maxLength: 2000,
                              },
                           },
                           {
                              key: 'supportEnterHomeWeeklyFlush',
                              type: 'radiobuttons',
                              props: {
                                 title: 'May we enter your home to perform a weekly flush to prevent Legionella?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                                 description:
                                    'In the event of the property being empty for 7 days or more (whether temporarily or permanent), do you agree to authorise staff to use the master key to enter your property to carry out the weekly flush so as to prevent Legionella',
                              },
                           },
                           {
                              expressions: {
                                 hide: (field: FormlyFieldConfig) => field.model.supportEnterHomeWeeklyFlush !== false,
                              },
                              key: 'supportEnterHomeWeeklyFlushReason',
                              type: 'textarea',
                              props: {
                                 required: true,
                                 title: 'Reason for refusal',
                                 maxLength: 2000,
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         //Employment Status
         {
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Employment Status' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'employmentSituationTypeId',
                              type: 'select',
                              props: {
                                 title: 'Which best describes your current employment status?',
                                 required: true,
                                 options: this.dataService.getReferenceDataByName(referenceTypes, 'cn_employmentsituationtype'),
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
         // Criminal Convictions (General Needs Version)
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'general'),
            },
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Criminal Convictions' },
            fieldGroup: [
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'unspentcriminalconvictions',
                              type: 'radiobuttons',
                              props: {
                                 title: 'For safety reasons: Do you or the joint applicant (if applicable) have any unspent criminal convictions?',
                                 required: true,
                                 labelHtmlClass: 'control-label',
                                 activeClass: 'active',
                                 options: this.yesNoMap,
                              },
                           },
                        ],
                     },
                  ],
               },
               {
                  wrappers: ['section'],
                  props: { htmlClass: 'row' },
                  expressions: {
                     hide: '!model.unspentcriminalconvictions',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        props: { htmlClass: 'col-sm-12' },
                        fieldGroup: [
                           {
                              key: 'unspentcriminalconvictionsNote',
                              type: 'input',
                              props: {
                                 required: true,
                                 title: 'Please give details (name, conviction, date, sentence)',
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
	 // Criminal Convictions (Ops version)
         {
            expressions: {
               hide: (field: FormlyFieldConfig) => !(field.model.applicationForm == 'ops'),
            },
            wrappers: ['connectSection'],
            props: { htmlClass: 'section', title: 'Criminal Convictions' },
            fieldGroup: [
               {
                  key: 'hasCriminalConviction',
                  type: 'radiobuttons',
                  props: {
                     title: 'Do you have any unspent criminal convictions?',
                     required: true,
                     labelHtmlClass: 'control-label',
                     activeClass: 'active',
                     options: this.yesNoMap,
                  },
               },
               {
                  expressions: {
                     hide: '!model.hasCriminalConviction',
                  },
                  fieldGroup: [
                     {
                        wrappers: ['section'],
                        fieldGroup: [
                           {
                              key: 'criminalConvictions',
                              defaultValue: [null],
                              type: 'array',
                              props: {
                                 add: 'Add More',
                                 itemTitle: 'Conviction',
                                 style: {
                                    add: 'btn-success',
                                 },
                                 // validationMessage: "",
                              },
                              fieldArray: {
                                 fieldGroup: [
                                    {
                                       wrappers: ['section'],
                                       props: { htmlClass: 'row' },
                                       fieldGroup: [
                                          //id?
                                          {
                                             wrappers: ['section'],
                                             props: { htmlClass: 'col-sm-6' },
                                             fieldGroup: [
                                                {
                                                   key: 'name',
                                                   type: 'input',
                                                   props: {
                                                      title: 'Name of Conviction',
                                                      required: true,
                                                   },
                                                },
                                             ],
                                          },
                                          {
                                             wrappers: ['section'],
                                             props: { htmlClass: 'col-sm-6' },
                                             fieldGroup: [
                                                {
                                                   key: 'sentenceDate',
                                                   type: 'datepicker',
                                                   props: {
                                                      title: 'Sentence Date',
                                                      required: true,
                                                      supportingText: 'dd/mm/yyyy',
                                                      minAge: 0,
                                                      maxAge: 120,
                                                   },
                                                },
                                             ],
                                          },
                                          {
                                             wrappers: ['section'],
                                             props: { htmlClass: 'col-sm-12' },
                                             fieldGroup: [
                                                {
                                                   key: 'details',
                                                   type: 'textarea',
                                                   props: {
                                                      required: true,
                                                      title: 'Please Provide Details of the Conviction.',
                                                   },
                                                },
                                             ],
                                          },
                                       ],
                                    },
                                 ],
                              },
                           },
                        ],
                     },
                  ],
               },
            ],
         },
      ];
   }
}
